import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import SignatureInput from "./SignatureInput";
import BackButton from "../components/BackButton";
import { displayName } from "../utils/capabilities";
import { isTaxCertExpired, isEffectivelyTaxExempt } from "../utils/billTotals";
import { projectedAnnualPrices } from "../utils/contracts";

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD" });

/**
 * Phase 5.0b — quote editor. Price-centric line items (labor, materials,
 * other charges, taxes/fees). Cost fields + the margin summary appear ONLY
 * when canSeeMargin (margin permission) — a tech without it just enters the
 * customer price. Saves to quote.costing via actions.updateQuote.
 */
export default function QuoteEditor({
  quote,
  actions,
  customer,
  customerName,
  locationName,
  canSeeMargin,
  quoteDefaults,
  orgId,
  onBack,
}) {
  // Org users for the contract Default-tech picker (maintenance quotes
  // only). Local listener so we don't add a global users hook just for
  // this — same pattern JobDetailPage uses.
  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(q, (snap) =>
      setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [orgId]);
  const fieldTechs = orgUsers.filter(
    (u) => !u.deleted && u.fieldEligible !== false
  );
  // Org-level defaults (Org Quote Defaults feature). Each toString()
  // normalizes the value -- inputs render best with "" rather than 0,
  // so the user can clear and re-type without seeing a stale 0.
  const dflt = (v) => {
    if (v === null || v === undefined) return "";
    return v === 0 ? "" : String(v);
  };
  const dfltLaborCost = dflt(quoteDefaults?.laborCost);
  const dfltLaborCharge = dflt(quoteDefaults?.laborCharge);
  const dfltMaterialMarkup = dflt(quoteDefaults?.materialMarkup);
  const dfltChargeMarkup = dflt(quoteDefaults?.chargeMarkup);
  const init = quote.costing || {};
  const isMaintenance = quote.quoteType === "maintenance";
  const [title, setTitle] = useState(quote.title || "");
  const [scope, setScope] = useState(quote.scope || "");
  const [exclusions, setExclusions] = useState(quote.exclusions || "");
  // Fall back to org defaults when the quote was created BEFORE the
  // admin configured Quote & Bill Defaults (so older quotes still get
  // the boilerplate in the editor instead of a blank field). User can
  // still delete or override on save.
  const [validDays, setValidDays] = useState(
    quote.validDays ?? quoteDefaults?.validDays ?? 30
  );
  const [terms, setTerms] = useState(
    quote.terms || quoteDefaults?.terms || ""
  );
  const [contractorName, setContractorName] = useState(
    quote.contractorSignature?.name || ""
  );
  const [contractorSig, setContractorSig] = useState("");
  const [labor, setLabor] = useState(init.labor || []);
  const [materials, setMaterials] = useState(init.materials || []);
  const [charges, setCharges] = useState(init.charges || []);
  const [fuel, setFuel] = useState(init.fuel || []);
  const [taxes, setTaxes] = useState(init.taxes || []);
  const [saving, setSaving] = useState(false);

  // Maintenance-only contract fields. Mirror the createContract defaults so a
  // brand-new maintenance quote opens with sensible values; existing fields
  // on the doc win on re-edit. Start date defaults to today (YYYY-MM-DD) so
  // the preview math has something real to chew on the first render.
  const todayYmd = new Date().toISOString().slice(0, 10);
  const [contractTermYears, setContractTermYears] = useState(
    quote.contractTermYears || 1
  );
  const [contractStartDate, setContractStartDate] = useState(
    quote.contractStartDate || todayYmd
  );
  const [contractVisitInterval, setContractVisitInterval] = useState(
    quote.contractVisitInterval || "quarterly"
  );
  const [contractBillingMode, setContractBillingMode] = useState(
    quote.contractBillingMode || "per-visit"
  );
  const [contractBillingInterval, setContractBillingInterval] = useState(
    quote.contractBillingInterval || "quarterly"
  );
  const [contractAnnualEscalationPct, setContractAnnualEscalationPct] =
    useState(quote.contractAnnualEscalationPct || "");
  const [contractDefaultTechId, setContractDefaultTechId] = useState(
    quote.contractDefaultTechId || ""
  );

  const approved = quote.status === "approved";

  const editLine = (setter) => (id, field, value) =>
    setter((p) => p.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  const removeLine = (setter) => (id) =>
    setter((p) => p.filter((l) => l.id !== id));

  const editLabor = editLine(setLabor);
  const editMaterial = editLine(setMaterials);
  const editCharge = editLine(setCharges);
  const editFuel = editLine(setFuel);
  const editTax = editLine(setTaxes);

  const addLabor = () =>
    setLabor((p) => [
      ...p,
      {
        id: newId(),
        description: "",
        hours: "",
        price: dfltLaborCharge,
        cost: dfltLaborCost,
        taxable: true,
      },
    ]);
  const addMaterial = () =>
    setMaterials((p) => [
      ...p,
      {
        id: newId(),
        name: "",
        partNumber: "",
        qty: "",
        cost: "",
        markup: dfltMaterialMarkup,
        taxable: true,
      },
    ]);
  const addCharge = () =>
    setCharges((p) => [
      ...p,
      {
        id: newId(),
        description: "",
        qty: "",
        cost: "",
        markup: dfltChargeMarkup,
        taxable: true,
      },
    ]);
  // Fuel / trip — same shape as JobCosting fuel. Mileage mode multiplies
  // miles-one-way × 2 (round trip) × trips × cost-per-mile; flat mode
  // is a single dollar amount. Markup % applies to either base.
  const addFuel = () =>
    setFuel((p) => [
      ...p,
      {
        id: newId(),
        mode: "mileage",
        description: "",
        costPerMile: "",
        milesOneWay: "",
        trips: "",
        amount: "",
        markup: "",
        taxable: true,
      },
    ]);
  const fuelLineCost = (f) =>
    f.mode === "flat"
      ? num(f.amount)
      : num(f.costPerMile) * num(f.milesOneWay) * 2 * num(f.trips);
  const fuelLineWithMarkup = (f) => fuelLineCost(f) * (1 + num(f.markup) / 100);

  // qty × (unit cost × markup) — the customer price for a material/charge line.
  const lineWithMarkup = (l) =>
    num(l.qty) * num(l.cost) * (1 + num(l.markup) / 100);
  const lineCost = (l) => num(l.qty) * num(l.cost);
  const addTax = () =>
    setTaxes((p) => [
      ...p,
      { id: newId(), name: "", mode: "percent", rate: "", amount: "" },
    ]);

  // --- totals (price = what the customer pays) ---
  const laborPrice = labor.reduce((s, l) => s + num(l.hours) * num(l.price), 0);
  const materialsPrice = materials.reduce((s, m) => s + lineWithMarkup(m), 0);
  const chargesPrice = charges.reduce((s, c) => s + lineWithMarkup(c), 0);
  const fuelPrice = fuel.reduce((s, f) => s + fuelLineWithMarkup(f), 0);
  const priceSubtotal = laborPrice + materialsPrice + chargesPrice + fuelPrice;

  // Tax block — same model as JobCosting bills:
  //   * Lines with taxable === false drop out of the taxable subtotal
  //     (rare; specific items the contractor flags as non-taxable).
  //   * Flat fees ignore taxableSubtotal.
  //   * Customer-level taxExempt forces every tax line to $0.00; the
  //     printable still renders a single "EXEMPT" row with the cert
  //     citation (see QuoteView print + [[project-tax-lines-required]]).
  const isTaxable = (line) => line?.taxable !== false;
  const laborTaxableSubtotal = labor.reduce(
    (s, l) => (isTaxable(l) ? s + num(l.hours) * num(l.price) : s),
    0
  );
  const materialsTaxableSubtotal = materials.reduce(
    (s, m) => (isTaxable(m) ? s + lineWithMarkup(m) : s),
    0
  );
  const chargesTaxableSubtotal = charges.reduce(
    (s, c) => (isTaxable(c) ? s + lineWithMarkup(c) : s),
    0
  );
  const fuelTaxableSubtotal = fuel.reduce(
    (s, f) => (isTaxable(f) ? s + fuelLineWithMarkup(f) : s),
    0
  );
  const taxableSubtotal =
    laborTaxableSubtotal +
    materialsTaxableSubtotal +
    chargesTaxableSubtotal +
    fuelTaxableSubtotal;

  // Effective exempt: taxExempt flag AND cert not expired. Expired cert
  // means tax applies until renewal — banner below renders on raw flag
  // with separate messaging for the expired case.
  const customerExempt = isEffectivelyTaxExempt(customer);
  const taxLineAmount = (t) => {
    if (customerExempt) return 0;
    return t.mode === "flat"
      ? num(t.amount)
      : taxableSubtotal * (num(t.rate) / 100);
  };
  const taxTotal = taxes.reduce((s, t) => s + taxLineAmount(t), 0);
  const grandTotal = priceSubtotal + taxTotal;

  // cost/margin — only meaningful with margin permission
  const totalCost =
    labor.reduce((s, l) => s + num(l.hours) * num(l.cost), 0) +
    materials.reduce((s, m) => s + lineCost(m), 0) +
    charges.reduce((s, c) => s + lineCost(c), 0) +
    fuel.reduce((s, f) => s + fuelLineCost(f), 0);
  const margin = priceSubtotal - totalCost; // gross profit ($)
  // Margin % is profit as a share of PRICE; markup % is profit as a share of
  // COST (always larger). Showing both avoids the classic estimator mix-up.
  const marginPct = priceSubtotal > 0 ? (margin / priceSubtotal) * 100 : 0;
  const markupPct = totalCost > 0 ? (margin / totalCost) * 100 : 0;

  const save = async () => {
    setSaving(true);
    // Preserve an existing signature image if the name is unchanged and they
    // didn't re-sign; stamp a new signedAt only when a fresh signature drawn.
    const sigImage = contractorSig || quote.contractorSignature?.image || "";
    const contractorSignature = contractorName.trim()
      ? {
          name: contractorName.trim(),
          title: "",
          image: sigImage,
          signedAt: contractorSig
            ? new Date().toISOString()
            : quote.contractorSignature?.signedAt ||
              new Date().toISOString(),
        }
      : null;
    // Maintenance fields only ride along on maintenance quotes so we
    // don't pollute one-time-job docs with empty contract metadata.
    const maintenancePatch = isMaintenance
      ? {
          contractTermYears: num(contractTermYears),
          contractStartDate,
          contractVisitInterval,
          contractBillingMode,
          contractBillingInterval,
          contractAnnualEscalationPct: num(contractAnnualEscalationPct) || 0,
          contractDefaultTechId: contractDefaultTechId || "",
        }
      : {};
    await actions.updateQuote(quote.id, {
      title,
      scope,
      exclusions,
      validDays: num(validDays),
      terms,
      contractorSignature,
      costing: { labor, materials, charges, fuel, taxes },
      ...maintenancePatch,
      updatedAt: new Date().toISOString(),
    });
    setSaving(false);
    onBack(); // return to the read-only quote view
  };

  const smallInput = { ...styles.input, width: "90px", margin: "2px" };
  const rm = (setter, id) => (
    <button
      style={{ ...styles.button, color: "red", padding: "2px 6px" }}
      onClick={() => removeLine(setter)(id)}
    >
      x
    </button>
  );

  return (
    <>
      <BackButton onClick={onBack} />
      {(quote.revision || 0) > 0 && (
        <div
          style={{
            padding: "8px 12px",
            background: "#fff3cd",
            border: "1px solid #ffd97a",
            borderRadius: "6px",
            color: "#7a5400",
            fontSize: "13px",
            marginBottom: "10px",
          }}
        >
          <strong>Editing Rev {quote.revision}</strong> — the previous
          version is saved in revision history. The contractor signature
          must be re-captured before re-sending; customer will see "Rev{" "}
          {quote.revision}" on the new PDF.
        </div>
      )}
      <h2 style={{ marginBottom: "2px" }}>
        {isMaintenance ? "Maintenance Quote" : "Quote"}
        {(quote.revision || 0) > 0 && (
          <span
            style={{
              marginLeft: "8px",
              padding: "2px 8px",
              borderRadius: "4px",
              background: "#fff3cd",
              color: "#9a6b00",
              fontSize: "13px",
              fontWeight: 700,
              verticalAlign: "middle",
            }}
          >
            Rev {quote.revision}
          </span>
        )}
      </h2>
      <div style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>
        {customerName || "—"}
        {locationName ? ` / ${locationName}` : ""} • Status:{" "}
        <strong>{quote.status}</strong>
        {isMaintenance && (
          <span
            style={{
              marginLeft: "8px",
              padding: "1px 6px",
              borderRadius: "4px",
              background: "#e0eaff",
              color: "#1a5fb4",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            PM CONTRACT
          </span>
        )}
      </div>

      {approved && (
        <div
          style={{
            color: "#1a7f37",
            fontWeight: "bold",
            fontSize: "13px",
            marginBottom: "8px",
          }}
        >
          Approved &amp; locked — this quote can no longer be edited.
        </div>
      )}

      <div style={styles.card}>
        <input
          style={styles.input}
          placeholder="Quote title / short description"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <textarea
          style={styles.textarea}
          placeholder="Scope of work (one item per line — renders as bullet points on the quote)"
          value={scope}
          onChange={(e) => setScope(e.target.value)}
        />
        <textarea
          style={styles.textarea}
          placeholder="Exclusions (one item per line — what's NOT included; renders as bullet points)"
          value={exclusions}
          onChange={(e) => setExclusions(e.target.value)}
        />
        <div style={{ fontSize: "13px", marginTop: "4px" }}>
          <label>Quote valid for </label>
          <input
            style={{ ...styles.input, width: "60px" }}
            type="number"
            value={validDays}
            onChange={(e) => setValidDays(e.target.value)}
          />
          <label> days</label>
        </div>
        <textarea
          style={styles.textarea}
          placeholder="Terms / conditions / notes (one item per line — renders as bullet points)"
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
        />

        {/* Maintenance contract block — visible ONLY for maintenance quotes.
            The line-item subtotal below becomes the ANNUAL scope price; this
            block locks term length + visit schedule + billing mode. Live
            preview math mirrors addContract (visitsPerYearFor + months/visit)
            so the user sees exactly what they're signing the customer up for. */}
        {isMaintenance && (() => {
          const visitsPerYear =
            { monthly: 12, quarterly: 4, biannual: 2, annual: 1 }[
              contractVisitInterval
            ] || 4;
          const termY = num(contractTermYears) || 1;
          const totalVisits = visitsPerYear * termY;
          const annual = priceSubtotal; // line-item subtotal is the annual price
          const perVisit = totalVisits > 0 ? annual / visitsPerYear : 0;
          const lifetime = annual * termY;
          // Backdated start date is a real workflow: contractor migrating an
          // existing customer onto the system mid-term. Calling it out so an
          // accidental typo doesn't slip through.
          const isBackdated =
            !!contractStartDate && contractStartDate < todayYmd;
          return (
            <div
              style={{
                marginTop: "12px",
                padding: "10px",
                background: "#f3f7ff",
                border: "1px solid #cfdcf5",
                borderRadius: "6px",
                fontSize: "13px",
              }}
            >
              <strong style={{ fontSize: "13px" }}>Contract details</strong>
              <div style={{ marginTop: "6px" }}>
                <label>Term: </label>
                <select
                  value={contractTermYears}
                  onChange={(e) => setContractTermYears(e.target.value)}
                  style={{ marginRight: "8px" }}
                >
                  <option value={1}>1 year</option>
                  <option value={2}>2 years</option>
                  <option value={3}>3 years</option>
                  <option value={5}>5 years</option>
                </select>
                <label>Start: </label>
                <input
                  type="date"
                  value={contractStartDate}
                  onChange={(e) => setContractStartDate(e.target.value)}
                  style={{ marginRight: "8px" }}
                />
              </div>
              <div style={{ marginTop: "4px" }}>
                <label>Visit interval: </label>
                <select
                  value={contractVisitInterval}
                  onChange={(e) => setContractVisitInterval(e.target.value)}
                  style={{ marginRight: "8px" }}
                >
                  <option value="monthly">Monthly (12/yr)</option>
                  <option value="quarterly">Quarterly (4/yr)</option>
                  <option value="biannual">Biannual (2/yr)</option>
                  <option value="annual">Annual (1/yr)</option>
                </select>
              </div>
              <div style={{ marginTop: "4px" }}>
                <label>Annual escalator %: </label>
                <input
                  type="number"
                  step="0.1"
                  value={contractAnnualEscalationPct}
                  onChange={(e) =>
                    setContractAnnualEscalationPct(e.target.value)
                  }
                  placeholder="0"
                  style={{ ...styles.input, width: "70px", margin: 0 }}
                  title="0 = flat pricing. Industry standard is 3–5% to cover wage / material / insurance inflation across multi-year contracts."
                />
                <span style={{ fontSize: "11px", color: "#666", marginLeft: "6px" }}>
                  (0 = flat; 3–5% standard for multi-year)
                </span>
              </div>
              <div style={{ marginTop: "4px" }}>
                <label>Default tech: </label>
                <select
                  value={contractDefaultTechId}
                  onChange={(e) => setContractDefaultTechId(e.target.value)}
                  style={{ marginRight: "8px" }}
                  title="Pre-assigned to every visit spawned by this contract once it's approved. Leave blank for dispatch to assign per visit. Office staff are filtered out."
                >
                  <option value="">
                    — None (dispatch assigns per visit) —
                  </option>
                  {fieldTechs.map((u) => (
                    <option key={u.id} value={u.id}>
                      {displayName(u.profile, u.email)} ({u.role})
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ marginTop: "4px" }}>
                <label>Billing mode: </label>
                <select
                  value={contractBillingMode}
                  onChange={(e) => setContractBillingMode(e.target.value)}
                  style={{ marginRight: "8px" }}
                >
                  <option value="per-visit">Per visit (bill on complete)</option>
                  <option value="per-interval">Per interval (bill on schedule)</option>
                </select>
                {contractBillingMode === "per-interval" && (
                  <>
                    <label>Bill every: </label>
                    <select
                      value={contractBillingInterval}
                      onChange={(e) =>
                        setContractBillingInterval(e.target.value)
                      }
                    >
                      <option value="monthly">Month</option>
                      <option value="quarterly">Quarter</option>
                      <option value="biannual">6 months</option>
                      <option value="annual">Year</option>
                    </select>
                  </>
                )}
              </div>
              {isBackdated && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "6px 8px",
                    background: "#fff5d6",
                    border: "1px solid #e0c060",
                    borderRadius: "4px",
                    color: "#6a4900",
                    fontSize: "12px",
                  }}
                >
                  <strong>Backdated start date.</strong> Past visits will be
                  spawned in <em>scheduled</em> status — flip each one to
                  <em> completed</em> or <em>skipped</em> from the contract
                  folder as you reconcile the migration.
                </div>
              )}
              <div
                style={{
                  marginTop: "8px",
                  paddingTop: "6px",
                  borderTop: "1px dashed #cfdcf5",
                  fontSize: "12px",
                  color: "#1a5fb4",
                }}
              >
                Spawns <strong>{totalVisits}</strong> visit
                {totalVisits === 1 ? "" : "s"} (
                {visitsPerYear}/yr × {termY} yr).{" "}
                {num(contractAnnualEscalationPct) > 0 ? (
                  <>Year-1 annual: <strong>{money(annual)}</strong> • Year-1 per-visit ≈{" "}
                  <strong>{money(perVisit)}</strong>.</>
                ) : (
                  <>Annual scope price:{" "}
                  <strong>{money(annual)}</strong> • Per-visit ≈{" "}
                  <strong>{money(perVisit)}</strong> • Lifetime contract value ≈{" "}
                  <strong>{money(lifetime)}</strong>.</>
                )}
              </div>
              {num(contractAnnualEscalationPct) > 0 && (() => {
                const projection = projectedAnnualPrices({
                  annualPrice: annual,
                  annualEscalationPct: num(contractAnnualEscalationPct),
                  termYears: termY,
                  visitInterval: contractVisitInterval,
                });
                const lifetimeEsc = projection.reduce(
                  (s, y) => s + y.annualPrice,
                  0
                );
                return (
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: "12px",
                      color: "#1a5fb4",
                    }}
                  >
                    <strong>
                      With {num(contractAnnualEscalationPct)}% escalator:
                    </strong>
                    <div
                      style={{
                        marginTop: "2px",
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fit, minmax(140px, 1fr))",
                        gap: "2px 12px",
                      }}
                    >
                      {projection.map((y) => (
                        <div key={y.year}>
                          Year {y.year}: <strong>{money(y.annualPrice)}</strong>{" "}
                          ({money(y.perVisitPrice)}/visit)
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: "4px" }}>
                      Lifetime contract value ≈{" "}
                      <strong>{money(lifetimeEsc)}</strong>
                    </div>
                  </div>
                );
              })()}
              <div
                style={{ marginTop: "4px", fontSize: "11px", color: "#666" }}
              >
                Use the line items below to price the <strong>annual</strong>{" "}
                scope (one year's worth of labor, materials, charges). The
                customer signs for that annual price × term-year commitment.
              </div>
            </div>
          );
        })()}

        {/* Labor */}
        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "13px" }}>Labor</strong>
          {labor.map((l) => (
            <div key={l.id} style={{ fontSize: "12px", marginTop: "2px" }}>
              <input
                style={{ ...smallInput, width: "150px" }}
                placeholder="Description"
                value={l.description}
                onChange={(e) => editLabor(l.id, "description", e.target.value)}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Hours"
                value={l.hours}
                onChange={(e) => editLabor(l.id, "hours", e.target.value)}
              />
              {canSeeMargin && (
                <input
                  style={smallInput}
                  type="number"
                  placeholder="Cost $/hr"
                  value={l.cost}
                  onChange={(e) => editLabor(l.id, "cost", e.target.value)}
                />
              )}
              <input
                style={smallInput}
                type="number"
                placeholder="Price $/hr"
                value={l.price}
                onChange={(e) => editLabor(l.id, "price", e.target.value)}
              />
              <span>= {money(num(l.hours) * num(l.price))}</span>{" "}
              <label style={{ fontSize: "11px", marginLeft: "4px" }}>
                <input
                  type="checkbox"
                  checked={l.taxable !== false}
                  onChange={(e) =>
                    editLabor(l.id, "taxable", e.target.checked)
                  }
                />{" "}
                Taxable
              </label>{" "}
              {rm(setLabor, l.id)}
            </div>
          ))}
          <button style={styles.button} onClick={addLabor}>
            + Labor
          </button>
        </div>

        {/* Materials */}
        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "13px" }}>Materials</strong>
          {materials.map((m) => (
            <div key={m.id} style={{ fontSize: "12px", marginTop: "2px" }}>
              <input
                style={{ ...smallInput, width: "130px" }}
                placeholder="Material"
                value={m.name}
                onChange={(e) => editMaterial(m.id, "name", e.target.value)}
              />
              <input
                style={smallInput}
                placeholder="Part #"
                value={m.partNumber}
                onChange={(e) => editMaterial(m.id, "partNumber", e.target.value)}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Qty"
                value={m.qty}
                onChange={(e) => editMaterial(m.id, "qty", e.target.value)}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Unit cost"
                value={m.cost}
                onChange={(e) => editMaterial(m.id, "cost", e.target.value)}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Markup %"
                value={m.markup}
                onChange={(e) => editMaterial(m.id, "markup", e.target.value)}
              />
              <span>= {money(lineWithMarkup(m))}</span>{" "}
              <label style={{ fontSize: "11px", marginLeft: "4px" }}>
                <input
                  type="checkbox"
                  checked={m.taxable !== false}
                  onChange={(e) =>
                    editMaterial(m.id, "taxable", e.target.checked)
                  }
                />{" "}
                Taxable
              </label>{" "}
              {rm(setMaterials, m.id)}
            </div>
          ))}
          <button style={styles.button} onClick={addMaterial}>
            + Material
          </button>
        </div>

        {/* Other charges */}
        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "13px" }}>Other Charges</strong>
          {charges.map((c) => (
            <div key={c.id} style={{ fontSize: "12px", marginTop: "2px" }}>
              <input
                style={{ ...smallInput, width: "160px" }}
                placeholder="Description (trip, rental, permit...)"
                value={c.description}
                onChange={(e) => editCharge(c.id, "description", e.target.value)}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Qty"
                value={c.qty}
                onChange={(e) => editCharge(c.id, "qty", e.target.value)}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Unit cost"
                value={c.cost}
                onChange={(e) => editCharge(c.id, "cost", e.target.value)}
              />
              <input
                style={smallInput}
                type="number"
                placeholder="Markup %"
                value={c.markup}
                onChange={(e) => editCharge(c.id, "markup", e.target.value)}
              />
              <span>= {money(lineWithMarkup(c))}</span>{" "}
              <label style={{ fontSize: "11px", marginLeft: "4px" }}>
                <input
                  type="checkbox"
                  checked={c.taxable !== false}
                  onChange={(e) =>
                    editCharge(c.id, "taxable", e.target.checked)
                  }
                />{" "}
                Taxable
              </label>{" "}
              {rm(setCharges, c.id)}
            </div>
          ))}
          <button style={styles.button} onClick={addCharge}>
            + Charge
          </button>
        </div>

        {/* Fuel / Trip — same mileage calculator as JobCosting. Mode
            "mileage" computes miles-one-way × 2 × trips × cost-per-mile;
            mode "flat" is a single dollar amount. Markup applies to
            either base. Sits above the tax block so fuel is part of the
            quoted scope. */}
        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "13px" }}>Fuel / Trip</strong>
          {fuel.map((f) => (
            <div key={f.id} style={{ fontSize: "12px", marginTop: "2px" }}>
              <select
                style={{ ...smallInput, width: "90px" }}
                value={f.mode || "mileage"}
                onChange={(e) => editFuel(f.id, "mode", e.target.value)}
              >
                <option value="mileage">Mileage</option>
                <option value="flat">Flat</option>
              </select>
              {f.mode === "flat" ? (
                <>
                  <input
                    style={{ ...smallInput, width: "160px" }}
                    placeholder="Description (e.g. Trip charge)"
                    value={f.description || ""}
                    onChange={(e) =>
                      editFuel(f.id, "description", e.target.value)
                    }
                  />
                  <input
                    style={smallInput}
                    type="number"
                    placeholder="$ amount"
                    value={f.amount || ""}
                    onChange={(e) => editFuel(f.id, "amount", e.target.value)}
                  />
                </>
              ) : (
                <>
                  <input
                    style={smallInput}
                    type="number"
                    placeholder="$/mile"
                    value={f.costPerMile || ""}
                    onChange={(e) =>
                      editFuel(f.id, "costPerMile", e.target.value)
                    }
                  />
                  <input
                    style={smallInput}
                    type="number"
                    placeholder="Miles 1-way"
                    value={f.milesOneWay || ""}
                    onChange={(e) =>
                      editFuel(f.id, "milesOneWay", e.target.value)
                    }
                  />
                  <input
                    style={smallInput}
                    type="number"
                    placeholder="Trips"
                    value={f.trips || ""}
                    onChange={(e) => editFuel(f.id, "trips", e.target.value)}
                  />
                </>
              )}
              {canSeeMargin && (
                <input
                  style={smallInput}
                  type="number"
                  placeholder="Markup %"
                  value={f.markup || ""}
                  onChange={(e) => editFuel(f.id, "markup", e.target.value)}
                />
              )}
              <span>= {money(fuelLineWithMarkup(f))}</span>{" "}
              <label style={{ fontSize: "11px", marginLeft: "4px" }}>
                <input
                  type="checkbox"
                  checked={f.taxable !== false}
                  onChange={(e) =>
                    editFuel(f.id, "taxable", e.target.checked)
                  }
                />{" "}
                Taxable
              </label>{" "}
              {rm(setFuel, f.id)}
            </div>
          ))}
          <button style={styles.button} onClick={addFuel}>
            + Fuel / Trip
          </button>
        </div>

        {/* Customer-level tax-exempt banner (same shape as JobCosting). */}
        {customer?.taxExempt && (() => {
          const certExpired = isTaxCertExpired(customer);
          return (
            <div
              style={{
                marginTop: "12px",
                padding: "8px 10px",
                background: certExpired ? "#fdecec" : "#fff8e0",
                border: certExpired ? "1px solid #e0a0a0" : "1px solid #f0d27a",
                borderRadius: "4px",
                fontSize: "12px",
                color: certExpired ? "#8a1f1f" : "#6a4900",
              }}
            >
              {certExpired ? (
                <>
                  <strong>TAX EXEMPT customer — cert expired.</strong>{" "}
                  Exemption is invalid until refreshed, so tax IS being
                  applied on this quote.
                  {customer?.taxExemptCert
                    ? ` Cert on file: ${customer.taxExemptCert}.`
                    : ""}
                </>
              ) : (
                <>
                  <strong>TAX EXEMPT customer</strong> — no sales tax will be
                  charged on this quote. The customer-facing quote will show
                  $0.00 with the cert citation.
                  {customer?.taxExemptCert
                    ? ` Cert: ${customer.taxExemptCert}.`
                    : " (Cert # not set on customer record.)"}
                </>
              )}
              {certExpired && (
                <div style={{ marginTop: "6px", fontWeight: 600 }}>
                  ⚠ Cert expired {customer.taxExemptCertExpires} — verify
                  with customer and refresh to restore exemption.
                </div>
              )}
            </div>
          );
        })()}

        {/* Taxes & fees */}
        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "13px" }}>Taxes &amp; Fees</strong>
          {taxes.map((t) => (
            <div key={t.id} style={{ fontSize: "12px", marginTop: "2px" }}>
              <input
                style={{ ...smallInput, width: "150px" }}
                placeholder="Name (Sales tax, EPA fee...)"
                value={t.name}
                onChange={(e) => editTax(t.id, "name", e.target.value)}
              />
              <select
                style={{ ...smallInput, width: "100px" }}
                value={t.mode}
                onChange={(e) => editTax(t.id, "mode", e.target.value)}
              >
                <option value="percent">% of price</option>
                <option value="flat">Flat fee</option>
              </select>
              {t.mode === "percent" ? (
                <input
                  style={smallInput}
                  type="number"
                  placeholder="Rate %"
                  value={t.rate}
                  onChange={(e) => editTax(t.id, "rate", e.target.value)}
                />
              ) : (
                <input
                  style={smallInput}
                  type="number"
                  placeholder="Amount $"
                  value={t.amount}
                  onChange={(e) => editTax(t.id, "amount", e.target.value)}
                />
              )}
              <span>= {money(taxLineAmount(t))}</span> {rm(setTaxes, t.id)}
            </div>
          ))}
          <button style={styles.button} onClick={addTax}>
            + Tax / fee
          </button>
        </div>

        {/* Contractor signature (authorizes the quote before sending) */}
        <div style={{ marginTop: "12px" }}>
          <strong style={{ fontSize: "13px" }}>Contractor signature</strong>
          <br />
          <input
            style={{ ...styles.input, width: "220px", margin: "2px 0" }}
            placeholder="Your name"
            value={contractorName}
            onChange={(e) => setContractorName(e.target.value)}
          />
          <SignatureInput onChange={setContractorSig} />
          {quote.contractorSignature?.image && !contractorSig && (
            <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
              A signature is already saved — re-sign only if you want to replace
              it.
            </div>
          )}
        </div>

        {/* Summary */}
        <div
          style={{
            marginTop: "14px",
            paddingTop: "10px",
            borderTop: "1px solid #ddd",
            fontSize: "14px",
          }}
        >
          <div>Price subtotal: {money(priceSubtotal)}</div>
          {taxTotal > 0 && <div>Taxes &amp; fees: {money(taxTotal)}</div>}
          <div style={{ fontWeight: "bold", fontSize: "15px" }}>
            {isMaintenance ? "Annual quote total: " : "Quote total: "}
            {money(grandTotal)}
          </div>
          {isMaintenance && (() => {
            const termY = num(contractTermYears) || 1;
            if (termY <= 1) return null;
            return (
              <div
                style={{ fontSize: "12px", color: "#1a5fb4", marginTop: "2px" }}
              >
                Lifetime contract value: {money(grandTotal * termY)} (
                {termY} yr × annual)
              </div>
            );
          })()}
          {canSeeMargin && (
            <div style={{ marginTop: "4px", color: "#666", fontSize: "13px" }}>
              <div>Cost: {money(totalCost)}</div>
              <div
                style={{
                  color: margin >= 0 ? "green" : "red",
                  fontWeight: "bold",
                }}
              >
                Gross profit: {money(margin)}
              </div>
              <div>
                Margin: {marginPct.toFixed(1)}% (of price) • Markup:{" "}
                {markupPct.toFixed(1)}% (of cost)
              </div>
            </div>
          )}
        </div>

        {!approved && (
          <div style={{ marginTop: "10px" }}>
            <button style={styles.button} onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save Quote"}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
